import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { listMyJoinedSpaces } from '@api-client/api-client';
import { spacesMyJoinedKey, type SpaceRole } from '@lib/cache/spaces';

const MY_JOINED_PER_PAGE = 20;

interface UseMyJoinedSpacesOptions {
  type?: string;
  /**
   * Server-side membership-role filter (added in backend#332). When set, the
   * endpoint only returns spaces where the current user holds this role.
   * Membership-role is part of the queryKey so admin / member views don't
   * collide in the cache.
   */
  role?: SpaceRole;
  enabled?: boolean;
  /**
   * Also return spaces where the viewer's membership is still PENDING (held posts, 2026-10).
   * Asked for by the two pickers that address a post to a Space, through
   * `COMPOSER_JOINED_SPACES`: the post composer, and the share drawer (TECH-683). A pending
   * member may post or share there, and the post is held until a host approves. Its own cache
   * slot (see spacesMyJoinedKey), so the approved-only callers never see pending rows.
   */
  includePending?: boolean;
}

/**
 * Infinite scroll hook for the user's joined spaces.
 * Mirrors useInfiniteCommunities but calls the /my-joined endpoint
 * (approved memberships; pass includePending for approved + pending).
 */
export const useMyJoinedSpaces = (options?: UseMyJoinedSpacesOptions) => {
  const { type, role, enabled = true, includePending = false } = options ?? {};

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    isLoadingError,
    isPaused,
    isPending,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: spacesMyJoinedKey({ type, role, includePending }),
    queryFn: async ({ pageParam }) => {
      const { data } = await listMyJoinedSpaces({
        type,
        role,
        page: pageParam,
        per_page: MY_JOINED_PER_PAGE,
        // Sent only when asked for, so every approved-only caller's request is unchanged.
        ...(includePending && { include_pending: true }),
      });
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.pagination.has_next) return undefined;
      return allPages.length + 1;
    },
  });

  const spaces = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.data);
  }, [data]);

  return {
    data: spaces,
    isLoading,
    isFetching,
    isError,
    // "Not known" is not "none". `data` above is [] both before anything has loaded and after a
    // list that really is empty has loaded, and `isError` is true both for a first load that
    // failed and for a refresh that failed over a loaded list. These three carry the
    // difference, for a caller that would otherwise tell a member with Spaces they have none:
    // - isLoadingError: the read failed and nothing has ever loaded;
    // - isPending: nothing has loaded yet;
    // - isPaused: the read cannot run because the device went offline (neither loading nor
    //   failed). Paused and pending together is "offline, with nothing cached".
    isLoadingError,
    isPaused,
    isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
